import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { GeckoFields, GECKO_CONSTS, GECKO_UTILS, GeckoAvatar } from 'ui';
import { CONSTS } from 'constants/index.js';
import CONSTANTS from 'chat-constants';
import TimePicker from 'components/TimePicker';
import GeckoDate from 'services/gecko-date';
import { allSelectableOptionsByValue } from 'utilities/customFieldUtils';
import { v4 } from 'uuid';

class Condition extends Component {
  static propTypes = {
    condition: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.any,
      operator: PropTypes.string,
    }),
    onChange: PropTypes.func,
    trigger: PropTypes.string,
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    // Check if key changed and if so make sure the operator can stay the same, if not then blank it and the value
    const newKey = nextProps.condition.key;
    if (
      !newKey &&
      (nextProps.condition.operator || nextProps.condition.value)
    ) {
      return nextProps.onChange({
        _id: v4(),
        key: newKey,
        operator: null,
        value: null,
      });
    }
    if (newKey !== this.props.condition.key) {
      const opTypes = this.getOperatorTypes(newKey);
      if (!opTypes.includes(nextProps.condition.operator)) {
        let newValue = [];
        if (
          newKey.indexOf('custom_') === 0 &&
          this.getCustomFieldType(newKey) === 'date'
        ) {
          newValue = Math.floor(Date.now() / 1000);
        }
        return nextProps.onChange(
          Object.assign({}, nextProps.condition, {
            operator: null,
            value: newValue,
          })
        );
      }
    }
    // Check if the value can stay the same, if not then blank it
    const oldFieldType = this.getFieldType(
      this.props.condition.key,
      this.props.condition.operator
    );
    const newFieldType = this.getFieldType(
      newKey,
      nextProps.condition.operator
    );
    if (oldFieldType !== newFieldType) {
      let newValue = [];
      if (newKey === 'date') {
        newValue = Math.floor(Date.now() / 1000);
      }
      return nextProps.onChange(
        Object.assign({}, nextProps.condition, { value: newValue })
      );
    }
  }

  typeMap() {
    // Types map directly to the operatorMap and fieldMap e.g. if a type is mapped to the 'text' value then the operators and field for text will be shown.
    return {
      channel: ['text', 'channel_id'],
      incoming_message: ['text'],
      conversation_status: ['convo_status'],
      any_agents_teams: ['agent_team_id'],
      any_agents_teams_uuid: ['agent_team_uuid'],
      contact_field_first_name: ['text', 'known'],
      contact_field_last_name: ['text', 'known'],
      contact_field_email: ['text', 'known'],
      contact_field_tel: ['text', 'known'],

      live_chat_channel: ['live_chat_channel_key'],
      referred_url: ['text', 'known'],
      browser_language: ['text', 'language', 'known'],
      device_type: ['device_type', 'known'],
      current_url: ['text', 'known'],
      page_visits: ['number', 'known'],
      time_on_page: ['time_on_page', 'known'],
      country: ['text', 'country', 'known'],
      state: ['text', 'known'],
      city: ['text', 'known'],

      current_time: ['time'],
      day_of_week: ['day_of_week'],
      date: ['date'],
    };
  }

  operatorMap() {
    return {
      text: [
        'equals',
        'not equals',
        'starts with',
        'not starts with',
        'ends with',
        'not ends with',
        'contains',
        'not contains',
      ],
      contains: ['contains', 'not contains'],
      known: ['is known', 'is unknown'],
      convo_status: ['equals', 'not equals'],
      number: ['equals', 'not equals', 'larger than', 'smaller than'],
      channel_id: ['equals', 'not equals'],
      live_chat_channel_key: ['equals', 'not equals'],
      agent_team_id: ['available', 'not available'],
      agent_team_uuid: ['available', 'not available'],
      agent_id: ['available', 'not available'],
      team_id: ['available', 'not available'],
      date: ['before', 'after'],
      select: ['equals', 'not equals'],
      select_multi: ['contains', 'not contains'],
      language: ['equals', 'not equals'],
      device_type: ['equals', 'not equals'],
      country: ['equals', 'not equals'],
      time_on_page: ['larger than', 'smaller than'],

      time: ['before', 'after'],
      day_of_week: ['equals', 'not equals'],
    };
  }

  fieldMap() {
    const pickedField = this.props.customFields.find(
      (f) => `custom_${f.id}` === this.props.condition.key
    ); // Used below for the select field
    return {
      text: {
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_CREATE_MULTI,
        options: [],
        width: '100',
        class: 'AddConditions-Condition-select-value Conditions-Field-Value',
      },
      channel_id: {
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
        options: this.props.channels
          .filter((channel) =>
            CONSTANTS.CHANNEL.PICKABLE_TYPES.includes(channel.type)
          )
          .map((channel) => {
            return { label: channel.name, value: channel.id };
          }),
        width: '100',
        class: 'AddConditions-Condition-select-value Conditions-Field-Value',
      },
      live_chat_channel_key: {
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
        options: this.props.channels
          .filter((c) => c.type === 'live_chat')
          .map((c) => {
            return { label: c.name, value: c.configuration.random_key };
          }),
        width: '100',
        class: 'AddConditions-Condition-select-value Conditions-Field-Value',
      },
      agent_team_id: {
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
        optionRenderer: (option) => {
          if (!option.agent && !option.team) {
            return option.label;
          } // If it's a group heading just return it
          if (option.agent) {
            // If it's an Agent then give it an avatar
            return (
              <div>
                <GeckoAvatar
                  size={'sm'}
                  name={option.agent.full_name}
                  color={option.agent.additional.avatar_colour}
                  bgImage={option.agent.additional.profile_picture_url}
                />{' '}
                {option.agent.full_name}
              </div>
            );
          } else {
            // If it's a Team then give it an avatar
            return (
              <div>
                <GeckoAvatar size={'sm'} name={option.team.name} />{' '}
                {option.team.name}
              </div>
            );
          }
        },
        valueRenderer: (option) => {
          if (!option.agent && !option.team) {
            return option.label;
          } // If it's a group heading just return it
          if (option.agent) {
            // If it's an Agent then give it an avatar
            return (
              <div>
                <GeckoAvatar
                  size={'sm'}
                  name={option.agent.full_name}
                  color={option.agent.additional.avatar_colour}
                  bgImage={option.agent.additional.profile_picture_url}
                />{' '}
                {option.agent.full_name}
              </div>
            );
          } else {
            // If it's a Team then give it an avatar
            return (
              <div>
                <GeckoAvatar size={'sm'} name={option.team.name} />{' '}
                {option.team.name}
              </div>
            );
          }
        },
        options: [
          {
            label: 'Agents',
            options: this.props.agents.map((a) => {
              return {
                agent: a,
                label: a.full_name,
                value: `agent_${a.id}`,
              };
            }),
          },
          {
            label: 'Teams',
            options: this.props.teams.map((t) => {
              return {
                team: t,
                label: t.name,
                value: `team_${t.id}`,
              };
            }),
          },
        ],
        width: '100',
        class: 'AddConditions-Condition-select-value Conditions-Field-Value',
      },
      agent_team_uuid: {
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
        optionRenderer: (option) => {
          if (!option.agent && !option.team) {
            return option.label;
          } // If it's a group heading just return it
          if (option.agent) {
            // If it's an Agent then give it an avatar
            return (
              <div>
                <GeckoAvatar
                  size={'sm'}
                  name={option.agent.full_name}
                  color={option.agent.additional.avatar_colour}
                  bgImage={option.agent.additional.profile_picture_url}
                />{' '}
                {option.agent.full_name}
              </div>
            );
          } else {
            // If it's a Team then give it an avatar
            return (
              <div>
                <GeckoAvatar size={'sm'} name={option.team.name} />{' '}
                {option.team.name}
              </div>
            );
          }
        },
        valueRenderer: (option) => {
          if (!option.agent && !option.team) {
            return option.label;
          } // If it's a group heading just return it
          if (option.agent) {
            // If it's an Agent then give it an avatar
            return (
              <div>
                <GeckoAvatar
                  size={'sm'}
                  name={option.agent.full_name}
                  color={option.agent.additional.avatar_colour}
                  bgImage={option.agent.additional.profile_picture_url}
                />{' '}
                {option.agent.full_name}
              </div>
            );
          } else {
            // If it's a Team then give it an avatar
            return (
              <div>
                <GeckoAvatar size={'sm'} name={option.team.name} />{' '}
                {option.team.name}
              </div>
            );
          }
        },
        options: [
          {
            label: 'Agents',
            options: this.props.agents.map((a) => {
              return {
                agent: a,
                label: a.full_name,
                value: `agent_${a.uuid}`,
              };
            }),
          },
          {
            label: 'Teams',
            options: this.props.teams.map((t) => {
              return {
                team: t,
                label: t.name,
                value: `team_${t.uuid}`,
              };
            }),
          },
        ],
        width: '100',
        class: 'AddConditions-Condition-select-value Conditions-Field-Value',
      },
      agent_id: {
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
        options: this.props.agents.map((a) => {
          return { label: a.full_name, value: a.id };
        }),
        width: '100',
        class: 'AddConditions-Condition-select-value Conditions-Field-Value',
      },
      team_id: {
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
        options: this.props.teams.map((t) => {
          return { label: t.name, value: t.id };
        }),
        width: '100',
        class: 'AddConditions-Condition-select-value Conditions-Field-Value',
      },
      convo_status: {
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
        options: [
          {
            label: 'New',
            value: 'new',
          },
          {
            label: 'Open',
            value: 'open',
          },
          {
            label: 'Closed',
            value: 'closed',
          },
        ],
        width: '100',
        class: 'AddConditions-Condition-select-value Conditions-Field-Value',
      },
      known: {
        key: 'value',
        hideWhen: () => true,
      },
      number: {
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_NUMBER,
        width: '100',
        class: 'AddConditions-Condition-select-value Conditions-Field-Value',
      },
      date: {
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_DATE,
        width: '100',
        format: 'X',
        display_format: new GeckoDate().FORMATS.SHORT,
        placeholder: new GeckoDate().FORMATS.SHORT,
        class: 'AddConditions-Condition-select-value Conditions-Field-Value',
      },
      time: {
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        component: <TimePicker />,
      },
      day_of_week: {
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
        options: [
          {
            label: 'Sunday',
            value: 'Sunday',
          },
          {
            label: 'Monday',
            value: 'Monday',
          },
          {
            label: 'Tuesday',
            value: 'Tuesday',
          },
          {
            label: 'Wednesday',
            value: 'Wednesday',
          },
          {
            label: 'Thursday',
            value: 'Thursday',
          },
          {
            label: 'Friday',
            value: 'Friday',
          },
          {
            label: 'Saturday',
            value: 'Saturday',
          },
        ],
        width: '100',
        class: 'AddConditions-Condition-select-value Conditions-Field-Value',
      },
      select: {
        key: 'value',
        placeholder: 'Pick some values',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
        options: pickedField
          ? allSelectableOptionsByValue(pickedField.field_options || [])
          : [],
        width: '100',
        class: 'AddConditions-Condition-select-value Conditions-Field-Value',
      },
      select_multi: {
        key: 'value',
        placeholder: 'Pick some values',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
        options: pickedField
          ? allSelectableOptionsByValue(pickedField.field_options || [])
          : [],
        width: '100',
        class: 'AddConditions-Condition-select-value Conditions-Field-Value',
      },
      language: {
        key: 'value',
        placeholder: 'Pick a language',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
        options: CONSTS.LANGUAGE_OPTIONS,
        width: '100',
        class: 'AddConditions-Condition-select-value Conditions-Field-Value',
      },
      device_type: {
        key: 'value',
        placeholder: 'Pick a device type',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
        options: [
          {
            label: 'Desktop',
            value: 'Desktop',
          },
          {
            label: 'Mobile',
            value: 'Mobile',
          },
          {
            label: 'Tablet',
            value: 'Tablet',
          },
        ],
        width: '100',
        class: 'AddConditions-Condition-select-value Conditions-Field-Value',
      },
      country: {
        key: 'value',
        placeholder: 'Pick a country',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
        options: CONSTS.COUNTRY_OPTIONS,
        width: '100',
        class: 'AddConditions-Condition-select-value Conditions-Field-Value',
      },
      time_on_page: {
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_NUMBER,
        width: '100',
        class: 'AddConditions-Condition-select-value Conditions-Field-Value',
      },
    };
  }

  getCustomFieldType(key) {
    const [_, id] = key.split('custom_');
    const field = this.props.customFields.find(
      (f) => Number(f.id) === Number(id)
    );
    return field ? field.type : 'text';
  }

  getOperatorTypes(key) {
    const typeMap = this.typeMap();
    const operatorMap = this.operatorMap();

    // If a custom field condition is picked we need to instead return the best operators for that field
    if (key && key.indexOf('custom_') === 0) {
      return (
        operatorMap[this.getCustomFieldType(key)] || operatorMap['text']
      ).concat(operatorMap['known']);
    }

    const types = typeMap[key];
    if (types) {
      return types.reduce((opTypes, type) => {
        return opTypes.concat(operatorMap[type]);
      }, []);
    } else {
      // defualt to text operators
      return operatorMap['text'];
    }
  }

  getFieldType(key, operator) {
    const operatorMap = this.operatorMap();

    // If a custom field condition is picked we need to instead return the best field for that field
    if (key && key.indexOf('custom_') === 0) {
      if (operatorMap['known'].includes(operator)) {
        return 'known';
      }
      const [_, id] = key.split('custom_');
      const field = this.props.customFields.find(
        (f) => Number(f.id) === Number(id)
      );
      return field ? field.type : 'text' || 'text';
    }

    const typeMap = this.typeMap();
    const types = typeMap[key];
    // Show a default field if we couldn't get any types
    if (!types) {
      return 'text';
    }

    const matchingTypes = types.filter((type) => {
      return operatorMap[type].includes(operator);
    });
    // Again show a default field if we couldn't get a specific type
    if (!matchingTypes.length) {
      return 'text';
    }

    // Types that aren't text should take precedence so grab the first one that isn't text or return text
    return matchingTypes.find((t) => t !== 'text') || 'text';
  }

  getTypeOptions() {
    const filterByTrigger = (options) => {
      return options
        .filter((option) => {
          // Remove any options that don't match the trigger
          if (option.validTriggers) {
            return option.validTriggers.indexOf(this.props.trigger) !== -1;
          }
          return true;
        })
        .map((option) => {
          // Ensure suboptions are put through the same checks
          if (!option.options) {
            return option;
          }
          return Object.assign({}, option, {
            options: filterByTrigger(option.options),
          });
        });
    };

    const conditions = [
      {
        label: 'Conversation',
        options: [
          {
            label: 'Channel',
            value: 'channel',
            validTriggers: ['message:new-conversation'],
          },
          {
            label: 'Last Used Channel',
            value: 'channel',
            validTriggers: ['conversation:closed'],
          },
          {
            label: 'Conversation Status',
            value: 'conversation_status',
            validTriggers: ['message:new-conversation'],
          },
          {
            label: 'First Incoming Message',
            value: 'incoming_message',
            validTriggers: ['message:new-conversation'],
          },
          {
            label: 'Last Message',
            value: 'last_message',
            validTriggers: ['conversation:closed'],
          },
        ],
      },
      {
        label: 'Availability',
        options: [
          {
            label: 'Current time',
            value: 'current_time',
            validTriggers: [
              'message:new-conversation',
              'conversation:closed',
              'widget:loaded',
            ],
          },
          {
            label: 'Day of the week',
            value: 'day_of_week',
            validTriggers: [
              'message:new-conversation',
              'conversation:closed',
              'widget:loaded',
            ],
          },
          {
            label: 'Date',
            value: 'date',
            validTriggers: [
              'message:new-conversation',
              'conversation:closed',
              'widget:loaded',
            ],
          },
          {
            label: 'Any of these Agents or Teams',
            value: 'any_agents_teams',
            validTriggers: ['message:new-conversation', 'conversation:closed'],
          },
          {
            label: 'Any of these Agents or Teams',
            value: 'any_agents_teams_uuid',
            validTriggers: ['widget:loaded'],
          },
        ],
      },
      {
        label: 'Live Chat Widget Data',
        options: [
          {
            label: 'Channel',
            value: 'live_chat_channel',
            validTriggers: ['widget:loaded'],
          },
          {
            label: 'Referred URL',
            value: 'referred_url',
            validTriggers: ['message:new-conversation', 'widget:loaded'],
          },
          {
            label: 'Browser language',
            value: 'browser_language',
            validTriggers: [
              'message:new-conversation',
              'widget:loaded',
              'conversation:closed',
            ],
          },
          {
            label: 'Device type',
            value: 'device_type',
            validTriggers: ['message:new-conversation', 'widget:loaded'],
          },
          {
            label: 'Current page URL',
            value: 'current_url',
            validTriggers: ['message:new-conversation', 'widget:loaded'],
          },
          {
            label: 'Number of page visits',
            value: 'page_visits',
            validTriggers: ['message:new-conversation', 'widget:loaded'],
          },
          {
            label: 'Time on page(seconds)',
            value: 'time_on_page',
            validTriggers: ['message:new-conversation', 'widget:loaded'],
          },
          {
            label: 'Country',
            value: 'country',
            validTriggers: [
              'message:new-conversation',
              'widget:loaded',
              'conversation:closed',
            ],
          },
          {
            label: 'City',
            value: 'city',
            validTriggers: [
              'message:new-conversation',
              'widget:loaded',
              'conversation:closed',
            ],
          },
          {
            label: 'State/Region',
            value: 'state',
            validTriggers: [
              'message:new-conversation',
              'widget:loaded',
              'conversation:closed',
            ],
          },
        ],
      },
      {
        label: 'Custom Fields',
        options: this.props.customFields.map((cf) => {
          return {
            label: cf.label,
            value: `custom_${cf.id}`,
            validTriggers: ['message:new-conversation', 'conversation:closed'],
          };
        }),
      },
      {
        label: 'Contact Fields',
        options: [
          {
            label: 'First Name',
            value: 'contact_field_first_name',
            validTriggers: ['message:new-conversation', 'conversation:closed'],
          },
          {
            label: 'Last Name',
            value: 'contact_field_last_name',
            validTriggers: ['message:new-conversation', 'conversation:closed'],
          },
          {
            label: 'Email',
            value: 'contact_field_email',
            validTriggers: ['message:new-conversation', 'conversation:closed'],
          },
          {
            label: 'Telephone',
            value: 'contact_field_tel',
            validTriggers: ['message:new-conversation', 'conversation:closed'],
          },
        ],
      },
      {
        label: 'Contact Data',
        options: [
          // {
          //     label: 'Notes',
          //     value: 'notes',
          // },
          // {
          //     label: 'Label',
          //     value: 'label'
          // },
          // {
          //     label: 'First seen',
          //     value: 'first_seen',
          // },
          // {
          //     label: 'Last seen',
          //     value: 'last_seen',
          // },
          // {
          //     label: 'Last contacted',
          //     value: 'last_contacted',
          // },
          // {
          //     label: 'Last heard from',
          //     value: 'last_heard_from',
          // },
        ],
      },
    ];

    return filterByTrigger(conditions);
  }

  getOperatorOptions() {
    const operators = [
      {
        label: 'Is equal to',
        value: 'equals',
      },
      {
        label: 'Is not equal to',
        value: 'not equals',
      },
      {
        label: 'Contains',
        value: 'contains',
      },
      {
        label: 'Does not contain',
        value: 'not contains',
      },
      {
        label: 'Starts with',
        value: 'starts with',
      },
      {
        label: 'Does not start with',
        value: 'not starts with',
      },
      {
        label: 'Ends with',
        value: 'ends with',
      },
      {
        label: 'Does not end with',
        value: 'not ends with',
      },
      {
        label: 'Is blank',
        value: 'blank',
      },
      {
        label: 'Is not blank',
        value: 'not blank',
      },
      {
        label: 'Is larger than',
        value: 'larger than',
      },
      {
        label: 'Is smaller than',
        value: 'smaller than',
      },
      {
        label: 'Is known',
        value: 'known',
      },
      {
        label: 'Is not known',
        value: 'not known',
      },
      {
        label: 'Is before',
        value: 'before',
      },
      {
        label: 'Is after',
        value: 'after',
      },
      {
        label: 'Are available',
        value: 'available',
      },
      {
        label: 'Are not available',
        value: 'not available',
      },
      {
        label: 'Is known',
        value: 'is known',
      },
      {
        label: 'Is unknown',
        value: 'is unknown',
      },
    ];

    const opTypes = this.getOperatorTypes(this.props.condition.key);
    return operators.filter((op) => opTypes.indexOf(op.value) !== -1);
  }

  getValueField() {
    const fieldMap = this.fieldMap();
    return (
      fieldMap[
        this.getFieldType(
          this.props.condition.key,
          this.props.condition.operator
        )
      ] || fieldMap['text']
    );
  }

  fields() {
    return [
      {
        key: 'key',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT,
        options: this.getTypeOptions(),
        class: 'AddConditions-Condition-select Conditions-Field-Option',
        width: '25%',
      },
      {
        key: 'operator',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT,
        options: this.getOperatorOptions(),
        class: 'AddConditions-Condition-select Conditions-Field-Operator',
        width: '15%',
      },
    ].concat(this.getValueField());
  }

  onChange(key, val) {
    return this.props.onChange(
      GECKO_UTILS.setNestedValue(this.props.condition, key, val)
    );
  }

  render() {
    if (!this.props.trigger) {
      return (
        <div className="ConfirmSetLive-Warning">
          <p>
            <i class="fas fa-exclamation-triangle"></i> You must select a
            Trigger in Step 2 before adding Conditions
          </p>
        </div>
      );
    }
    return (
      <div className="AddConditions-Condition row">
        <GeckoFields
          fields={this.fields()}
          values={this.props.condition}
          handler={this.onChange.bind(this)}
        />
        <div
          className="AddConditions-Group-add-condition"
          onClick={this.props.addCondition}
        >
          <i className="far fa-plus"></i>
        </div>
        <div
          className="AddConditions-Condition-remove"
          onClick={this.props.removeCondition}
        >
          <i className="far fa-trash-alt"></i>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    agents: state.visible_agents,
    channels: state.channels.filter((channel) => !channel.deletedAt),
    teams: state.teams,
    customFields: state.fields.filter((f) => f.custom_field === true),
  };
};

export default connect(mapStateToProps)(Condition);
